import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  PurchaseOrderItem,
  PurchaseOrderItemStatus,
} from '@domain/purchasing';
import PurchaseOrderItemRow from './PurchaseOrderItemRow';
import {
  StatusMilestone,
  getFinalEta,
  getLatestOverdueMilestone,
  getStatusEndDate,
} from './utils';

interface PurchaseOrderOverdueDialogProps {
  open: boolean;
  items: PurchaseOrderItem[];
  today: Date;
  updatingId: string | null;
  onClose: () => void;
  onOpen: (item: PurchaseOrderItem) => void;
  onConfirm: (item: PurchaseOrderItem, milestone: StatusMilestone) => void;
  onDelayed: (item: PurchaseOrderItem) => void;
  onAddShipment: (item: PurchaseOrderItem) => void;
  onChangeStatus: (
    item: PurchaseOrderItem,
    status: PurchaseOrderItemStatus
  ) => void;
}

const PurchaseOrderOverdueDialog: React.FC<PurchaseOrderOverdueDialogProps> = ({
  open,
  items,
  today,
  updatingId,
  onClose,
  onOpen,
  onConfirm,
  onDelayed,
  onAddShipment,
  onChangeStatus,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6" component="span">
            Overdue items ({items.length})
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer>
          <Table sx={{ width: '100%' }}>
            <TableBody>
              {items.map((item) => {
                const milestone: StatusMilestone =
                  getLatestOverdueMilestone(item, today) ?? {
                    date: getStatusEndDate(item) ?? getFinalEta(item)!,
                    kind: 'eta',
                  };
                return (
                  <PurchaseOrderItemRow
                    key={
                      item.id ??
                      `${item.product.id}-${milestone.date.toISOString()}`
                    }
                    item={item}
                    milestone={milestone}
                    today={today}
                    isUpdating={updatingId === item.id}
                    onOpen={(opened) => {
                      onClose();
                      onOpen(opened);
                    }}
                    onConfirm={onConfirm}
                    onDelayed={onDelayed}
                    onAddShipment={onAddShipment}
                    onChangeStatus={onChangeStatus}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseOrderOverdueDialog;
